import { Marker } from 'react-leaflet';
import { shallowEqual } from 'react-redux';
import Map from '../../../../../../shared/components/map/Map';
import { getMarkerIcon } from '../../../../adMarkerBuilder';
import { dpeMarkerUrl, dpeCombinedMarkerUrl } from '../../../../constant';
import { useAppSelector } from '../../../../../../App/redux/store';
import { getAdState } from '../../../../adSlice';
import styles from './similarDpeResult.module.scss';

import { FormControlLabel, Radio, Tooltip } from '@material-ui/core';
import { ReactComponent as PictoPlace } from '../../../../../../images/picto-place.svg';
import { ReactComponent as PictoSurface } from '../../../../../../images/picto-surface.svg';
import StreetViewLink from '../similarPlotsResult/StreetViewLink';
import { useRadioButtonStyles } from '../similarPlotsResult/muiStyles';
import UrbaneaseButton from '../../../../../../components/linksToUrbanease/UrbaneaseButton';

interface ISimilarDpeResultCardProps {
  mergedAdDpe: MergedAdDpe;
  isHighlighted: boolean;
  onRadioClick: (address: string) => void;
}

function SimilarDpeResultCard({
  mergedAdDpe,
  isHighlighted,
  onRadioClick,
}: ISimilarDpeResultCardProps) {
  const { similarAdPlots } = useAppSelector(getAdState, shallowEqual);
  const coord = mergedAdDpe.coordinates;

  if (!coord || !mergedAdDpe.address) return null;

  const department = mergedAdDpe.inseeCode.substring(0, 2);

  // Determine if this DPE should use the combined marker
  const plotAddresses = new Set(
    (similarAdPlots.result ?? [])
      .map((p) => p.address?.toLowerCase().trim())
      .filter(Boolean),
  );
  const hasMatchingPlot = plotAddresses.has(
    mergedAdDpe.address.toLowerCase().trim(),
  );
  const markerUrl = hasMatchingPlot ? dpeCombinedMarkerUrl : dpeMarkerUrl;

  return (
    <div
      id={mergedAdDpe.uniqueDpeId}
      className={`${styles.similarDpeResultCard} ${
        isHighlighted ? styles.highlighted : ''
      }`}
    >
      <Map
        center={mergedAdDpe.coordinates}
        className={styles.map}
        isSatellite
        zoom={16}
        markers={[
          <Marker
            key={mergedAdDpe.uniqueDpeId}
            position={[coord.lat, coord.lng]}
            icon={getMarkerIcon(markerUrl)}
          />,
        ]}
      />
      <div className={styles.infos}>
        <p>
          <PictoPlace width="16" height="20" />{' '}
          <span>
            {mergedAdDpe.isGeoAddress
              ? 'Lieu'
              : 'Adresse renseignée par le diagnostiqueur'}
          </span>
          {':'}
          {mergedAdDpe.address ? (
            <StreetViewLink
              lat={coord.lat}
              lng={coord.lng}
              address={mergedAdDpe.address}
            />
          ) : (
            'indéfinie'
          )}
        </p>
        <p>
          <PictoSurface width="16" height="20" />
          <span>Surface</span>
          {` : ${mergedAdDpe.landArea} m²`}
        </p>
        <div className={styles.dpeDataContainer}>
          {mergedAdDpe.isGeoAddress && (
            <div className={styles.dpeDataRow}>
              <div className={styles.labelWithInfo}>
                <span className={styles.label}>Score de fiabilité:</span>
                <Tooltip
                  title="Le score de fiabilité évalue la précision de la géolocalisation du DPE. Il varie de 0 à 1, où 1 indique une localisation très précise et 0 une localisation incertaine. Ce score est calculé en analysant la cohérence entre l'adresse déclarée et les coordonnées géographiques."
                  arrow
                  placement="top"
                  classes={{ tooltip: styles.tooltip }}
                >
                  <span className={styles.infoIcon}>?</span>
                </Tooltip>
              </div>
              <span className={styles.value}>
                {mergedAdDpe.geoScore?.toFixed(1)}
              </span>
            </div>
          )}
          <div className={styles.dpeEntriesContainer}>
            <span className={styles.label}>
              DPE enregistrés ({mergedAdDpe.dpeEntries.length}):
            </span>
            <div className={styles.dpeEntriesList}>
              {mergedAdDpe.dpeEntries.map((entry, index) => (
                <div key={index} className={styles.dpeEntry}>
                  <div className={styles.dpeEntryRow}>
                    <span className={styles.entryLabel}>N° DPE:</span>
                    <span className={styles.entryValue}>
                      {entry.numDpe || 'Non renseigné'}
                    </span>
                  </div>
                  <div className={styles.dpeEntryRow}>
                    <span className={styles.entryLabel}>Date:</span>
                    <span className={styles.entryValue}>
                      {entry.dateDpe
                        ? new Date(entry.dateDpe).toLocaleDateString('fr-FR')
                        : 'Non renseignée'}
                    </span>
                  </div>
                  <div className={styles.dpeEntryRow}>
                    <span className={styles.entryLabel}>DPE:</span>
                    {entry.dpe ? (
                      <span
                        className={`${styles.entryValue} ${styles.energyValue} ${
                          entry.dpe ? styles[`energy${entry.dpe}`] : ''
                        }`}
                      >
                        {entry.dpe}
                      </span>
                    ) : (
                      <span className={styles.entryValue}>Non renseigné</span>
                    )}
                  </div>
                  <div className={styles.dpeEntryRow}>
                    <span className={styles.entryLabel}>GES:</span>
                    {entry.ges ? (
                      <span
                        className={`${styles.entryValue} ${styles.energyValue} ${
                          entry.ges ? styles[`energy${entry.ges}`] : ''
                        }`}
                      >
                        {entry.ges}
                      </span>
                    ) : (
                      <span className={styles.entryValue}>Non renseigné</span>
                    )}
                  </div>
                  <div className={styles.dpeEntryRow}>
                    <span className={styles.entryLabel}>Surface:</span>
                    <span className={styles.entryValue}>
                      {entry.surface ? `${entry.surface} m²` : 'Non renseignée'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <FormControlLabel
          value={mergedAdDpe.address}
          control={
            <Radio
              classes={useRadioButtonStyles()}
              onClick={() => onRadioClick(mergedAdDpe.address)}
            />
          }
          label="Sélectionnez cette adresse pour enregistrer l'annonce"
        />
        <UrbaneaseButton department={department} lat={coord.lat} lng={coord.lng} />
      </div>
    </div>
  );
}

export default SimilarDpeResultCard;
